import { Suspense, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { IMask } from "react-imask"
import { zodResolver } from "@hookform/resolvers/zod"
import { ClockCounterClockwise, House, Phone } from "@phosphor-icons/react"
import { t } from "i18next"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { FormField } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tag } from "@/components/ui/tag"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { PostalCodeSelector } from "@/modules/inbound-wizard/components/contact/address/postal-code-selector"
import { EmergencyCitySelector } from "@/modules/patients/components/header/emergency-service/components/emergency-city-selector"
import {
  isValidCountry,
  SWITZERLAND_COUNTRY_CODE,
} from "@/modules/patients/components/header/emergency-service/lib/isValidCountry"
import {
  EmergencyServiceSchema,
  EmergencyServiceSchemaType,
} from "@/modules/patients/components/header/emergency-service/schema/emergency-service-schema"
import { useChEmergencyPhoneNumbersQuery$data } from "@/modules/patients/components/header/emergency-service/services/ch/__generated__/useChEmergencyPhoneNumbersQuery.graphql"
import { useChEmergencyPhoneNumbersQuery } from "@/modules/patients/components/header/emergency-service/services/ch/useChEmergencyPhoneNumbersQuery"
import { useConsultEmergencyServiceMutation } from "@/modules/patients/components/header/emergency-service/services/ch/useConsultEmergencyServiceMutation"
import { usePhoneNumberCountryList } from "@/modules/patients/components/phoneNumber/hooks/usePhoneNumberCountryList"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { usePatientAgenda } from "@/modules/patients/services/usePatientAgenda"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { useGenesysTelephony } from "@/modules/telephony/hooks/useGenesysTelephony"
import { usePatientPhoneNumbersOnCall } from "@/modules/telephony/hooks/usePatientPhoneNumbersOnCall"

const HOME_ADDRESS = "HOME_ADDRESS"
const MANUAL_ADDRESS = "MANUAL_ADDRESS"

type EmergencyPhoneNumbers = useChEmergencyPhoneNumbersQuery$data["ch_emergencyPhoneNumbers"]

type PatientAddressList = {
  value: string
  label: string
  postalCode?: string
  countryCode?: string
  icon?: React.JSX.Element
}[]

type EmergencyServiceDialogProps = {
  onOpenChange: (open: boolean) => void
}

const AmbulanceIcon = ({ isCritical = false }: { isCritical?: boolean }) => {
  return (
    <svg
      width="24"
      height={isCritical ? "24" : "28"}
      viewBox="0 0 24 28"
      className="shrink-0"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.0625 3.59155V1.09155C10.0625 0.825928 10.1524 0.603219 10.3322 0.423428C10.512 0.243844 10.7347 0.154053 11.0003 0.154053C11.2661 0.154053 11.4888 0.243844 11.6681 0.423428C11.8477 0.603219 11.9375 0.825928 11.9375 1.09155V3.59155C11.9375 3.85697 11.8476 4.07957 11.6678 4.25937C11.488 4.43895 11.2653 4.52874 10.9997 4.52874C10.7339 4.52874 10.5112 4.43895 10.3319 4.25937C10.1523 4.07957 10.0625 3.85697 10.0625 3.59155ZM4.41344 5.49999L2.43281 3.53124C2.24698 3.34332 2.15406 3.12009 2.15406 2.86155C2.15406 2.60301 2.24698 2.38072 2.43281 2.19468C2.61865 2.00884 2.8375 1.91593 3.08938 1.91593C3.34104 1.91593 3.56125 2.00884 3.75 2.19468L5.73813 4.1828C5.92396 4.36864 6.01885 4.58822 6.02281 4.84155C6.02677 5.09468 5.93385 5.31416 5.74406 5.49999C5.55406 5.68582 5.33135 5.77874 5.07594 5.77874C4.82031 5.77874 4.59948 5.68582 4.41344 5.49999ZM0.375 16.9856C0.375 16.8558 0.383437 16.726 0.400312 16.5962C0.417187 16.4664 0.446458 16.343 0.488125 16.2259L2.92781 9.31249C3.02094 9.02562 3.19167 8.7977 3.44 8.62874C3.68833 8.45957 3.96479 8.37499 4.26937 8.37499H7.82687V7.23312C7.82687 6.9352 7.92479 6.68239 8.12062 6.47468C8.31646 6.26718 8.55906 6.16343 8.84844 6.16343H10.0366C10.2582 6.16343 10.4205 6.24916 10.5234 6.42062C10.6264 6.59207 10.6249 6.77478 10.5191 6.96874C10.3332 7.36791 10.1834 7.78332 10.0697 8.21499C9.95573 8.64666 9.88365 9.09103 9.85344 9.54811C9.83406 9.73978 9.75708 9.9027 9.6225 10.0369C9.48792 10.171 9.32448 10.2381 9.13219 10.2381H4.58656L2.65875 15.4903H12.6059C12.8716 15.4903 13.0942 15.5802 13.2738 15.76C13.4533 15.9398 13.5431 16.1626 13.5431 16.4284C13.5431 16.6941 13.4533 16.9167 13.2738 17.0962C13.0942 17.2756 12.8716 17.3653 12.6059 17.3653H2.25V22.75H19.75V19.5887C19.75 19.3231 19.8399 19.1005 20.0197 18.9209C20.1995 18.7413 20.4222 18.6516 20.6878 18.6516C20.9536 18.6516 21.1763 18.7413 21.3556 18.9209C21.5352 19.1005 21.625 19.3231 21.625 19.5887V25.8028C21.625 26.1701 21.4964 26.4823 21.2391 26.7394C20.9816 26.9964 20.6691 27.125 20.3016 27.125C19.9339 27.125 19.6218 26.9964 19.3653 26.7394C19.1091 26.4823 18.9809 26.1701 18.9809 25.8028V24.625H3.01906V25.8028C3.01906 26.1701 2.89042 26.4823 2.63312 26.7394C2.37583 26.9964 2.06333 27.125 1.69562 27.125C1.32812 27.125 1.01615 26.9964 0.759687 26.7394C0.503229 26.4823 0.375 26.1701 0.375 25.8028V16.9856ZM7.41844 21.1394C7.7249 21.1394 7.98177 21.0357 8.18906 20.8284C8.39635 20.6211 8.5 20.3644 8.5 20.0581C8.5 19.7517 8.39635 19.4947 8.18906 19.2872C7.98177 19.0797 7.7249 18.9759 7.41844 18.9759H4.94219C4.63573 18.9759 4.37885 19.0796 4.17156 19.2869C3.96427 19.4942 3.86062 19.7509 3.86062 20.0572C3.86062 20.3636 3.96427 20.6206 4.17156 20.8281C4.37885 21.0356 4.63573 21.1394 4.94219 21.1394H7.41844ZM14.5816 21.1394H17.0578C17.3643 21.1394 17.6211 21.0357 17.8284 20.8284C18.0357 20.6211 18.1394 20.3644 18.1394 20.0581C18.1394 19.7517 18.0357 19.4947 17.8284 19.2872C17.6211 19.0797 17.3643 18.9759 17.0578 18.9759H14.5816C14.2751 18.9759 14.0182 19.0796 13.8109 19.2869C13.6036 19.4942 13.5 19.7509 13.5 20.0572C13.5 20.3636 13.6036 20.6206 13.8109 20.8281C14.0182 21.0356 14.2751 21.1394 14.5816 21.1394ZM17.1731 10.7909L16.3941 10.0122C16.2468 9.86468 16.0742 9.79207 15.8763 9.79436C15.6783 9.79686 15.5017 9.87582 15.3463 10.0312C15.1906 10.1867 15.1128 10.3613 15.1128 10.5553C15.1128 10.7493 15.1906 10.9239 15.3463 11.0794L16.3822 12.1153C16.6082 12.3413 16.8719 12.4544 17.1731 12.4544C17.4744 12.4544 17.7379 12.3413 17.9638 12.1153L20.7884 9.30999C20.9359 9.1627 21.0117 8.99009 21.0156 8.79218C21.0196 8.59426 20.9439 8.41759 20.7884 8.26218C20.633 8.10655 20.4583 8.03197 20.2644 8.03843C20.0704 8.04489 19.8957 8.11947 19.7403 8.26218L17.1731 10.7909ZM18.0672 4.61312C19.6282 4.61312 20.9559 5.1603 22.0503 6.25468C23.1449 7.34926 23.6922 8.67707 23.6922 10.2381C23.6922 11.7989 23.1449 13.1267 22.0503 14.2212C20.9559 15.3156 19.6282 15.8628 18.0672 15.8628C16.5064 15.8628 15.1786 15.3156 14.0841 14.2212C12.9895 13.1267 12.4422 11.7989 12.4422 10.2381C12.4422 8.67707 12.9895 7.34926 14.0841 6.25468C15.1786 5.1603 16.5064 4.61312 18.0672 4.61312Z"
        className={isCritical ? "fill-critical" : "fill-on-plum-surface"}
      />
    </svg>
  )
}

const patientInstructionWarnings = [
  {
    key: "technical-issue",
    value: t("patient.header.emergencyServiceDialog.technicalIssue"),
  },
  {
    key: "patient-is-abroad",
    value: t("patient.header.emergencyServiceDialog.patientIsAbroad"),
  },
]

const EmergencyServiceDialog = ({ onOpenChange }: EmergencyServiceDialogProps) => {
  const { t, i18n } = useTranslation()
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const appointmentId = useAppointmentParam()
  const [patientRef] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const emergencyPhoneNumbersData = useChEmergencyPhoneNumbersQuery()
  const { isGenesysLoginInFlight, openGenesysLoginWindow, runViaGenesysIfEnabled } = useGenesysTelephony()
  const [consultEmergencyService, isConsultingEmergencyService] = useConsultEmergencyServiceMutation()
  const { getCountryByPhoneNumber } = usePhoneNumberCountryList("")
  const { agendaData } = usePatientAgenda(countryCode, patientId, i18n.language)
  const countryPatient = patientRef.patientById
  const { address, phoneNumber: patientPhoneNumber } = countryPatient || {}
  const { city, country: countryObject, houseNumberStreet, postalCode: homeAddressPostalCode } = address || {}
  const homeAddressCountryCode = countryObject?.code ?? SWITZERLAND_COUNTRY_CODE
  const phoneNumbersOnCall = usePatientPhoneNumbersOnCall()

  const getPatientAddressList = () => {
    const houseNumberStreetInfo = joinStrings([`${t("patient.agenda.address.homeAddress")}:`, houseNumberStreet], " ")
    const postalCodeCity = joinStrings([homeAddressPostalCode, city], " ")
    const homeAddressLabel = joinStrings([houseNumberStreetInfo, postalCodeCity, countryObject?.code], ", ")

    const patientAddressList: PatientAddressList = [
      {
        value: HOME_ADDRESS,
        label: homeAddressLabel,
        postalCode: homeAddressPostalCode,
        countryCode: homeAddressCountryCode,
        icon: (
          <House className="text-on-surface-subdued group-data-[state='checked']:text-on-primary-surface mr-1.5 inline h-6 w-6" />
        ),
      },
    ]

    if (appointmentId) {
      agendaData?.appointments?.edges?.forEach(({ node }) => {
        const { id, patientAddress } = node
        if (appointmentId === id && patientAddress && Object.values(patientAddress).some((v) => !!v)) {
          const { houseNumberStreet, postalCode, city, countryCode } = patientAddress
          const addressInfo = joinStrings(
            [
              `${t("patient.header.emergencyServiceDialog.addedManually")}: ${houseNumberStreet}`,
              `${postalCode} ${city}`,
              countryCode,
            ],
            ", ",
          )

          patientAddressList.push({
            value: `${id} ${addressInfo}`,
            label: addressInfo,
            postalCode,
            countryCode,
            icon: (
              <ClockCounterClockwise className="text-on-surface-subdued group-data-[state='checked']:text-on-primary-surface mr-1.5 inline h-6 w-6" />
            ),
          })
        }
      })
    }

    patientAddressList.push({
      value: MANUAL_ADDRESS,
      label: t(`patient.agenda.address.manually`),
    })

    return patientAddressList
  }

  const patientAddressOptions = getPatientAddressList()

  const addedManualAddress = appointmentId
    ? patientAddressOptions.find((item) => item.value.includes(appointmentId))
    : null

  const filteredPhoneNumbers: EmergencyPhoneNumbers = emergencyPhoneNumbersData.filter((item) =>
    addedManualAddress
      ? isValidCountry(addedManualAddress.countryCode) && item.postalCode === addedManualAddress.postalCode
      : isValidCountry(homeAddressCountryCode) && item.postalCode === homeAddressPostalCode,
  )
  const [emergencyPhoneNumbers, setEmergencyPhoneNumbers] = useState<EmergencyPhoneNumbers>(filteredPhoneNumbers)

  const form = useForm<EmergencyServiceSchemaType>({
    mode: "all",
    resolver: zodResolver(EmergencyServiceSchema),
    defaultValues: {
      patientAddress: addedManualAddress ? addedManualAddress.value : HOME_ADDRESS,
      country: SWITZERLAND_COUNTRY_CODE,
      postalCode: "",
      city: "",
      houseNumberStreet: "",
    },
  })

  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors: formErrors },
  } = form
  const [patientAddress, manualAddressPostalCode] = watch(["patientAddress", "postalCode"])
  const selectedPatientAddress = patientAddressOptions.find((item) => item.value === patientAddress)

  const findOptionLabel = (value?: string) =>
    patientAddressOptions.find((option) => option.value === value)?.label ?? ""

  const handleEmergencyPhoneNumbers = (postalCode: string = "") => {
    if (postalCode) setEmergencyPhoneNumbers(emergencyPhoneNumbersData.filter((item) => item.postalCode === postalCode))
    else setEmergencyPhoneNumbers([])
  }

  const formatPhoneNumberByCountryMask = () => {
    const phoneNumber =
      agendaData?.appointments?.edges?.find(({ node }) => node.id === appointmentId)?.node.patientPhoneNumber ??
      patientPhoneNumber

    if (phoneNumber) {
      const country = getCountryByPhoneNumber(phoneNumber)

      const masked = IMask.createMask({
        mask: country?.mask,
      })
      masked.resolve(phoneNumber)

      return masked.value || phoneNumber
    }
  }

  const formattedPhoneNumber = formatPhoneNumberByCountryMask()

  const callEmergencyServiceWithGenesys = (phone: string) =>
    consultEmergencyService(phone, countryPatient?.patientId, phoneNumbersOnCall, countryCode, () =>
      openGenesysLoginWindow(() => callEmergencyServiceWithGenesys(phone)),
    )

  const callEmergencyServiceWithPhoneClient = (phone: string) => {
    window.disableConfirmBeforeLeaving = true

    setTimeout(() => {
      window.disableConfirmBeforeLeaving = false
    }, 500)

    window.location.href = `callto:${phone.replaceAll(" ", "")}`
  }

  const handleCallClick = (phone: string) =>
    runViaGenesysIfEnabled(
      () => callEmergencyServiceWithGenesys(phone),
      () => callEmergencyServiceWithPhoneClient(phone),
    )

  const renderEmergencyPhoneNumbers = () => {
    if (emergencyPhoneNumbers.length === 0) {
      return <Tag type="critical" label={t("patient.header.emergencyServiceDialog.missingEmergencyNumber")} />
    }

    return emergencyPhoneNumbers.map(({ id, name, phoneNumber }) => (
      <div className="flex justify-between gap-4" key={id}>
        <div className="flex items-center">
          <Button className="bg-plum-surface hover:bg-plum-surface active:bg-plum-surface mr-2 h-10 w-10 cursor-default">
            <AmbulanceIcon />
          </Button>
          <label className="text-on-surface text-sm">{name}</label>
        </div>
        <Button
          className="shrink-0"
          data-testid="call-emergency-service-button"
          variant="primary"
          disabled={isGenesysLoginInFlight || isConsultingEmergencyService}
          onClick={() => handleCallClick(phoneNumber)}>
          <Phone />
          {t("patient.header.emergencyServiceDialog.callEmergencyService")}
        </Button>
      </div>
    ))
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <FormProvider {...form}>
        <DialogContentCustom
          variant="small"
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="emergency-service-content"
          className="flex min-h-155 flex-col">
          <DialogHeader>
            <DialogTitle data-testid="emergency-service-dialog-title">
              {t("patient.header.emergencyServiceDialog.title")}
            </DialogTitle>
          </DialogHeader>
          <div className="mx-6 my-4 flex flex-col gap-4">
            <p className="text-on-surface leading-6 font-medium">
              {t("patient.header.emergencyServiceDialog.confirmCurrentLocation")}
            </p>
            <FormField
              control={control}
              name="patientAddress"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  value={value}
                  onValueChange={(value) => {
                    const selectedAddress = patientAddressOptions.find((item) => item.value === value)
                    if (value !== MANUAL_ADDRESS && isValidCountry(selectedAddress?.countryCode)) {
                      setEmergencyPhoneNumbers(
                        emergencyPhoneNumbersData.filter((item) => item.postalCode === selectedAddress?.postalCode),
                      )
                    } else if (value === MANUAL_ADDRESS && manualAddressPostalCode) {
                      setEmergencyPhoneNumbers(
                        emergencyPhoneNumbersData.filter((item) => item.postalCode === manualAddressPostalCode),
                      )
                    } else setEmergencyPhoneNumbers([])

                    onChange(value)
                  }}
                  {...ref}>
                  <SelectTrigger>
                    <SelectValue asChild placeholder={t("patient.agenda.address.location")}>
                      <LabelAndSelectValue
                        required
                        data-testid="patient-address-select"
                        label={t("patient.agenda.address.location")}
                        value={findOptionLabel(value)}
                      />
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent
                    className="max-w-(--radix-select-trigger-width)"
                    data-testid="patient-address-select-content">
                    <ScrollArea viewportClassName="max-h-78.75">
                      <div className="py-2">
                        {patientAddressOptions.map((option) => (
                          <SelectItem
                            className={cn(
                              "group",
                              option.value === MANUAL_ADDRESS ? "text-primary focus:text-primary" : "",
                            )}
                            key={option.value}
                            value={option.value}>
                            {option.icon} {option.label}
                          </SelectItem>
                        ))}
                      </div>
                    </ScrollArea>
                  </SelectContent>
                </Select>
              )}
            />
            {patientAddress === MANUAL_ADDRESS && (
              <>
                <p className="text-on-surface text-sm">
                  {t("patient.header.emergencyServiceDialog.switzerlandOrLiechtenstein")}
                </p>
                <div className="grid w-full grid-cols-4 gap-4">
                  <Suspense fallback={null}>
                    <FormField
                      control={control}
                      name="postalCode"
                      render={({ field: { value, onChange } }) => (
                        <PostalCodeSelector
                          className="col-span-2"
                          countryIsoCode={SWITZERLAND_COUNTRY_CODE}
                          label={`${t("patient.header.emergencyServiceDialog.zipCode")}*`}
                          value={value}
                          onSelect={(data) => {
                            if (data) {
                              setValue("city", data.city ?? "", { shouldValidate: true, shouldDirty: true })
                            }
                            handleEmergencyPhoneNumbers(data?.value)
                          }}
                          onChange={(value) => onChange(value ?? "")}
                          error={t(formErrors?.postalCode?.message ?? "")}
                        />
                      )}
                    />
                    <FormField
                      control={control}
                      name="city"
                      render={({ field: { value, onChange } }) => (
                        <EmergencyCitySelector
                          required
                          className="col-span-2"
                          countryIsoCode={SWITZERLAND_COUNTRY_CODE}
                          value={value}
                          onSelect={(data) => {
                            if (data) {
                              setValue("postalCode", data.postalCode ?? "", { shouldValidate: true, shouldDirty: true })
                              handleEmergencyPhoneNumbers(data.postalCode)
                            }
                          }}
                          onChange={(value) => onChange(value ?? "")}
                          error={t(formErrors?.city?.message ?? "")}
                        />
                      )}
                    />
                  </Suspense>
                  <Input
                    data-testid="house-number-street"
                    label={t("patient.header.emergencyServiceDialog.streetAddress")}
                    wrapperProps={{ className: "col-span-4" }}
                    error={t(formErrors?.houseNumberStreet?.message ?? "")}
                    {...register("houseNumberStreet")}
                  />
                </div>
              </>
            )}

            {formattedPhoneNumber && (
              <div>
                <p
                  data-testid="phone-number-title"
                  className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]">
                  {t("common.phoneNumber")} ({t("patient.header.emergencyServiceDialog.consultation")})
                </p>
                <p data-testid="phone-number" className="text-on-surface text-sm">
                  {formattedPhoneNumber}
                </p>
              </div>
            )}

            <p className="text-on-surface mt-4 leading-6 font-medium">
              2. {t("patient.header.emergencyServiceDialog.callEmergencyService")}
            </p>
            <Banner variant="warning" type="inline">
              <ul className="ml-[0.563rem]">
                {patientInstructionWarnings.map(({ key, value }) => (
                  <li
                    key={key}
                    className="before:bg-on-warning-surface relative indent-3 text-sm before:absolute before:top-2 before:left-0 before:size-1 before:rounded-full">
                    {value}
                  </li>
                ))}
              </ul>
            </Banner>
            {((patientAddress !== MANUAL_ADDRESS && isValidCountry(selectedPatientAddress?.countryCode)) ||
              (patientAddress === MANUAL_ADDRESS && !!manualAddressPostalCode)) &&
              renderEmergencyPhoneNumbers()}
          </div>
          <DialogFooter className="mt-auto flex p-6" data-testid="emergency-service-dialog-footer">
            <Button
              className="px-4 py-2"
              variant="secondary"
              onClick={() => {
                onOpenChange(false)
              }}
              data-testid="close-button">
              {t("common.button.close")}
            </Button>
          </DialogFooter>
        </DialogContentCustom>
      </FormProvider>
    </Dialog>
  )
}

export { EmergencyServiceDialog, AmbulanceIcon, MANUAL_ADDRESS }
